import {
    Alert, Box, Button, CircularProgress, Divider,
    Grid, Stack, TextField, Typography
} from "@mui/material";
import { Email as EmailIcon, CalendarMonth as CalendarMonthIcon } from '@mui/icons-material';
import { useState } from "react";
import { api } from "../api/apiClient";
import { usePageMeta } from "../hooks/usePageMeta";

export function ContactPage() {
    usePageMeta({
        title: 'Contact',
        description: 'Get in touch with ColbrenSolutions. Tell us about your business process challenges and we will find a practical AI-powered solution.',
    });

    const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
    const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async () => {
        if (!form.name || !form.email || !form.message) return;
        setStatus('sending');
        try {
            const res = await api.post('/api/contact', { ...form, appName: 'Colbren Solutions UI' });
            if (res.ok) {
                setStatus('success');
                setForm({ name: '', email: '', company: '', message: '' });
            } else {
                setStatus('error');
            }
        } catch {
            setStatus('error');
        }
    };

    return (
        <>
            <Box sx={{ mb: 4 }}>
                <Typography variant='h3' sx={{ fontWeight: 700, mb: 1 }}>Let's Talk</Typography>
                <Typography variant='body1' color='text.secondary' sx={{ maxWidth: 560 }}>
                    Tell me what's slowing your team down. A 20-minute conversation is usually enough
                    to know whether I can help — and what that would look like.
                </Typography>
            </Box>

            <Grid container spacing={6}>
                {/* Contact Form */}
                <Grid item xs={12} md={7}>
                    <Stack spacing={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label='Your Name'
                                    name='name'
                                    value={form.name}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    size='small'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label='Email Address'
                                    name='email'
                                    type='email'
                                    value={form.email}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    size='small'
                                />
                            </Grid>
                        </Grid>
                        <TextField
                            label='Company / Organization (optional)'
                            name='company'
                            value={form.company}
                            onChange={handleChange}
                            fullWidth
                            size='small'
                        />
                        <TextField
                            label='What are you dealing with? The more detail the better.'
                            name='message'
                            value={form.message}
                            onChange={handleChange}
                            fullWidth
                            required
                            multiline
                            rows={5}
                            size='small'
                        />

                        {status === 'success' && (
                            <Alert severity='success'>Got it — I'll be in touch within 1 business day.</Alert>
                        )}
                        {status === 'error' && (
                            <Alert severity='error'>Something went wrong. Email me directly at info@colbrensolutions.com</Alert>
                        )}

                        <Button
                            variant='contained'
                            size='large'
                            onClick={handleSubmit}
                            disabled={status === 'sending' || !form.name || !form.email || !form.message}
                            sx={{ fontWeight: 600, alignSelf: 'flex-start', px: 4 }}
                        >
                            {status === 'sending' ? <CircularProgress size={22} color='inherit' /> : 'Send Message'}
                        </Button>
                    </Stack>
                </Grid>

                {/* Right side — direct contact + scheduling */}
                <Grid item xs={12} md={5}>
                    <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: 2, p: 3 }}>
                        <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>Other ways to reach me</Typography>

                        <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 2 }}>
                            <EmailIcon color='primary' />
                            <Box>
                                <Typography variant='body2' color='text.secondary'>Email</Typography>
                                <Typography variant='body1'>
                                    <a href='mailto:info@colbrensolutions.com' style={{ color: '#1565c0' }}>
                                        info@colbrensolutions.com
                                    </a>
                                </Typography>
                            </Box>
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Stack direction='row' alignItems='flex-start' spacing={1.5} sx={{ mb: 2 }}>
                            <CalendarMonthIcon color='primary' sx={{ mt: 0.3 }} />
                            <Box>
                                <Typography variant='body2' color='text.secondary'>Schedule a call</Typography>
                                <Typography variant='body2' sx={{ mb: 1.5, lineHeight: 1.6 }}>
                                    Prefer to pick a time directly? Book a free 20-minute intro call.
                                </Typography>
                                {/* Replace CALENDLY_URL with your actual Calendly link */}
                                <Button
                                    variant='outlined'
                                    size='small'
                                    href='https://calendly.com/jeff-colbrensolutions/20-minute-meeting'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    Book a Time
                                </Button>
                            </Box>
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>Or scan to visit the site</Typography>
                            <Box component='img' src='/images/qrcode.png' sx={{ width: '120px' }} alt='QR Code' />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};
